import Reference from 'bibliography/domain/Reference'
import { Joins } from 'fragmentarium/domain/join'
import { immerable } from 'immer'
import _ from 'lodash'
import { Period, PeriodModifier, PeriodModifiers } from 'common/utils/period'
import { Periods } from 'common/utils/periodDefinitions'
import {
  compareAssyriaAndBabylonia,
  compareName,
  compareStandardText,
  getProvenanceByName,
  Provenance,
} from 'corpus/domain/provenance'

export const ManuscriptTypes = {
  None: { name: 'None', abbreviation: '', displayName: '-', order: null },
  Library: { name: 'Library', abbreviation: '', displayName: null, order: 1 },
  School: { name: 'School', abbreviation: 'Sch', displayName: null, order: 2 },
  Varia: { name: 'Varia', abbreviation: 'Var', displayName: null, order: null },
  Commentary: {
    name: 'Commentary',
    abbreviation: 'Com',
    displayName: null,
    order: null,
  },
  Quotation: {
    name: 'Quotation',
    abbreviation: 'Quo',
    displayName: null,
    order: 5,
  },
  Amulet: { name: 'Amulet', abbreviation: 'Amu', displayName: null, order: 3 },
  Excerpt: { name: 'Excerpt', abbreviation: 'Ex', displayName: null, order: 4 },
  Parallel: {
    name: 'Parallel',
    abbreviation: 'Par',
    displayName: null,
    order: 6,
  },
  'Multi-column tablet': {
    name: 'Multi-column tablet',
    abbreviation: 'MultCol',
    displayName: null,
    order: 7,
  },
  'Collective tablet': {
    name: 'Collective tablet',
    abbreviation: 'Coll',
    displayName: null,
    order: 8,
  },
  'Student-teacher tablet': {
    name: 'Student-teacher tablet',
    abbreviation: 'StuTea',
    displayName: null,
    order: 9,
  },
  'School lentils': {
    name: 'School lentils',
    abbreviation: 'SchLen',
    displayName: null,
    order: 10,
  },
  Prisms: {
    name: 'Prisms',
    abbreviation: 'Prism',
    displayName: null,
    order: 11,
  },
  Uncertain: {
    name: 'Uncertain',
    abbreviation: 'Unc',
    displayName: null,
    order: null,
  },
} as const

export type ManuscriptType =
  (typeof ManuscriptTypes)[keyof typeof ManuscriptTypes]
export const types = [
  ManuscriptTypes.None,
  ManuscriptTypes.Library,
  ManuscriptTypes.School,
  ManuscriptTypes.Varia,
  ManuscriptTypes.Commentary,
  ManuscriptTypes.Quotation,
  ManuscriptTypes.Amulet,
  ManuscriptTypes.Excerpt,
  ManuscriptTypes.Parallel,
  ManuscriptTypes['Multi-column tablet'],
  ManuscriptTypes['Collective tablet'],
  ManuscriptTypes['Student-teacher tablet'],
  ManuscriptTypes['School lentils'],
  ManuscriptTypes.Prisms,
  ManuscriptTypes.Uncertain,
] as const

export function compareManuscriptTypes(
  first: ManuscriptType,
  second: ManuscriptType,
): number {
  if (_.isNil(first.order) && _.isNil(second.order)) {
    return 0
  } else if (_.isNil(first.order)) {
    return 1
  } else if (_.isNil(second.order)) {
    return -1
  } else {
    return Math.sign(first.order - second.order)
  }
}

export class OldSiglum {
  readonly [immerable] = true

  constructor(
    readonly siglum: string,
    readonly reference: Reference,
  ) {}
}

export interface ManuscriptProps {
  readonly id: number | null
  readonly siglumDisambiguator: string
  readonly oldSigla: OldSiglum[]
  readonly museumNumber: string
  readonly accession: string
  readonly periodModifier: PeriodModifier
  readonly period: Period
  readonly provenance: Provenance
  readonly type: ManuscriptType
  readonly notes: string
  readonly colophon: string
  readonly unplacedLines: string
  readonly references: readonly Reference[]
  readonly joins: Joins
  readonly isInFragmentarium: boolean
}

export class Manuscript {
  readonly [immerable] = true
  readonly id: number | null
  readonly siglumDisambiguator: string
  readonly oldSigla: OldSiglum[]
  readonly museumNumber: string
  readonly accession: string
  readonly periodModifier: PeriodModifier
  readonly period: Period
  readonly provenance: Provenance
  readonly type: ManuscriptType
  readonly notes: string
  readonly colophon: string
  readonly unplacedLines: string
  readonly references: readonly Reference[]
  readonly joins: Joins
  readonly isInFragmentarium: boolean

  constructor({
    id = null,
    siglumDisambiguator = '',
    oldSigla = [],
    museumNumber = '',
    accession = '',
    periodModifier = PeriodModifiers.None,
    period = Periods['Neo-Assyrian'],
    provenance = getProvenanceByName('Nineveh'),
    type = ManuscriptTypes.Library,
    notes = '',
    colophon = '',
    unplacedLines = '',
    references = [],
    joins = [],
    isInFragmentarium = false,
  }: Partial<ManuscriptProps> = {}) {
    this.id = id
    this.siglumDisambiguator = siglumDisambiguator
    this.oldSigla = oldSigla
    this.museumNumber = museumNumber
    this.accession = accession
    this.periodModifier = periodModifier
    this.period = period
    this.provenance = provenance
    this.type = type
    this.notes = notes
    this.colophon = colophon
    this.unplacedLines = unplacedLines
    this.references = references
    this.joins = joins
    this.isInFragmentarium = isInFragmentarium
  }

  get siglum(): string {
    return [
      _.get(this, 'provenance.abbreviation', ''),
      _.get(this, 'period.abbreviation', ''),
      _.get(this, 'type.abbreviation', ''),
      this.siglumDisambiguator,
    ].join('')
  }
}

export function compareManuscripts(
  first: Pick<Manuscript, 'provenance' | 'type'>,
  second: Pick<Manuscript, 'provenance' | 'type'>,
): number {
  return (
    compareStandardText(first.provenance, second.provenance) ||
    compareManuscriptTypes(first.type, second.type) ||
    compareAssyriaAndBabylonia(first.provenance, second.provenance) ||
    compareName(first.provenance, second.provenance)
  )
}
